import { useState, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { FileText, Upload, Loader2, CheckCircle2, ShieldAlert, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const SOPRules = () => {
  const fileInputRef = useRef(null);
  const [filename, setFilename] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [extracted, setExtracted] = useState(null); // {filename, rules: [...]}
  const [selected, setSelected] = useState({}); // index -> bool

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFilename(f.name);
    setExtracting(true);
    setExtracted(null);
    setSelected({});
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/sop/extract`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExtracted(res.data);
      // Pre-select all rules by default
      const initSel = {};
      (res.data.rules || []).forEach((_, i) => { initSel[i] = true; });
      setSelected(initSel);
      if ((res.data.rules || []).length === 0) {
        toast.info("No rules could be extracted from this document.");
      } else {
        toast.success(`Found ${res.data.rules.length} candidate rule(s)`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Extraction failed";
      toast.error(msg);
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const toggle = (idx) => setSelected(s => ({ ...s, [idx]: !s[idx] }));

  const apply = async () => {
    if (!extracted) return;
    const chosen = (extracted.rules || []).filter((_, i) => selected[i]);
    if (chosen.length === 0) {
      toast.error("Select at least one rule to add");
      return;
    }
    setApplying(true);
    try {
      const res = await axios.post(`${API}/sop/apply`, { rules: chosen }, { withCredentials: true });
      toast.success(`Added ${res.data.created} rule(s) to your roster engine`);
      setExtracted(null);
      setFilename("");
      setSelected({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add rules");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="sop-page">
        <div>
          <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">
            SOP / Policy Rules
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Upload your Standard Operating Procedure or policy document and let AI extract roster rules automatically.
          </p>
        </div>

        {/* Upload card */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-5 h-5 text-[#002FA7]" />
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Upload SOP Document
            </h2>
          </div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
            Supported formats: <span className="font-medium">PDF, DOCX, TXT, MD</span>. AI will read the document and propose
            scheduling rules — you review and accept what to keep.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,.md"
              onChange={onFileChange}
              className="hidden"
              data-testid="sop-file-input"
            />
            <Button
              onClick={onPickFile}
              disabled={extracting}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="sop-upload-btn"
            >
              {extracting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting…
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" /> Choose file
                </>
              )}
            </Button>
            {filename && (
              <span className="text-sm text-zinc-600 dark:text-zinc-400 flex items-center gap-1.5">
                <FileText className="w-4 h-4" /> {filename}
              </span>
            )}
          </div>
        </div>

        {/* Extracted rules */}
        {extracted && (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid="sop-extracted">
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                  AI suggested {extracted.rules?.length || 0} rule(s)
                </h3>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  Review each one and uncheck anything you don't want.
                </p>
              </div>
              <Button
                onClick={apply}
                disabled={applying || !(extracted.rules?.length)}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="sop-apply-btn"
              >
                {applying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Add selected rules
                  </>
                )}
              </Button>
            </div>
            {(extracted.rules?.length || 0) === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
                <ShieldAlert className="w-8 h-8 mx-auto mb-2 text-zinc-400" />
                The AI did not find any extractable scheduling rules in this document.
              </div>
            ) : (
              <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {extracted.rules.map((r, idx) => (
                  <label
                    key={idx}
                    className="flex items-start gap-3 p-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 cursor-pointer"
                    data-testid={`sop-rule-${idx}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!selected[idx]}
                      onChange={() => toggle(idx)}
                      className="mt-1 w-4 h-4 accent-[#002FA7]"
                      data-testid={`sop-rule-check-${idx}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          {r.name}
                        </span>
                        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          r.is_hard
                            ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                            : "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                        }`}>
                          {r.is_hard ? "Hard" : "Soft"}
                        </span>
                        <span className="text-[10px] uppercase tracking-wide bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 px-1.5 py-0.5 rounded">
                          {r.rule_type}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1 break-words">
                        {Object.entries(r.value || {}).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ") || "—"}
                      </p>
                      {r.source_quote && (
                        <p className="text-[11px] italic text-zinc-500 dark:text-zinc-400 mt-1 border-l-2 border-zinc-200 dark:border-zinc-700 pl-2">
                          "{r.source_quote}"
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SOPRules;
